import React, { useState } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment, useMutation } from 'react-relay';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import Lozenge from '@atlaskit/lozenge';
import { ButtonItem } from '@atlaskit/menu';
import Popup from '@atlaskit/popup';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { approvalStatus_softwareReleasesApprovers$key } from '@atlassian/jira-relay/src/__generated__/approvalStatus_softwareReleasesApprovers.graphql';
import type {
	JiraVersionApproverStatus,
	approvalStatus_softwareReleasesApproversMutation$data,
	approvalStatus_softwareReleasesApproversMutation,
} from '@atlassian/jira-relay/src/__generated__/approvalStatus_softwareReleasesApproversMutation.graphql';
import {
	STATUS_APPROVED_BUTTON_CLICK_IGNORE_TARGET,
	STATUS_DECLINED_BUTTON_CLICK_IGNORE_TARGET,
	STATUS_PENDING_BUTTON_CLICK_IGNORE_TARGET,
	STATUS_POPUP_TRIGGER_CLICK_IGNORE_TARGET,
} from '../../../common/constants';
import {
	useChangeVersionApproversStatusStart,
	useChangeVersionApproversStatusFail,
	useChangeVersionApproversStatusSuccess,
} from '../../../services/status-experience-tracker';
import messages from './messages';

interface Props {
	jiraVersionApprover: approvalStatus_softwareReleasesApprovers$key;
}

export const ApprovalStatus = ({ jiraVersionApprover }: Props) => {
	const { formatMessage } = useIntl();
	const [isStatusOpen, setIsStatusOpen] = useState(false);
	const experienceStart = useChangeVersionApproversStatusStart();
	const experienceFail = useChangeVersionApproversStatusFail();
	const experienceSuccess = useChangeVersionApproversStatusSuccess();
	const { showFlag } = useFlagsService();
	const {
		data: { user },
	} = useCurrentUser();

	const approver = useFragment(
		graphql`
			fragment approvalStatus_softwareReleasesApprovers on JiraVersionApprover {
				id @required(action: THROW)
				status @required(action: THROW)
				user @required(action: THROW) {
					accountId @required(action: THROW)
				}
			}
		`,
		jiraVersionApprover,
	);

	const approverAri = approver.id;
	const canChangeStatus = user.accountId === approver.user.accountId;
	const isApproved = approver.status === 'APPROVED';
	const isDeclined = approver.status === 'DECLINED';

	const mapApprovalStateToLozengeAppearance = (state: JiraVersionApproverStatus) => {
		switch (state) {
			case 'APPROVED':
				return 'success';
			case 'DECLINED':
				return 'removed';
			default:
				return 'default';
		}
	};

	const [updateApproverStatus] = useMutation<approvalStatus_softwareReleasesApproversMutation>(
		graphql`
			mutation approvalStatus_softwareReleasesApproversMutation(
				$input: JiraVersionUpdateApproverStatusInput!
			) {
				jira {
					updateJiraVersionApproverStatus(input: $input)
						@optIn(to: "JiraVersionUpdateApproverStatus") {
						success
						approver {
							id
							status
						}
						errors {
							message
						}
					}
				}
			}
		`,
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'button',
	});
	const handleClick = () => {
		fireUIAnalytics(analyticsEvent, 'versionApproverStatusPopup');
		if (!canChangeStatus) {
			return;
		}
		setIsStatusOpen(!isStatusOpen);
	};

	const onMutationError = (error: Error) => {
		experienceFail('graphql error', error);
		fireErrorAnalytics({
			meta: {
				id: 'jira.releases.approvers.task-approver.approver-status',
				packageName: 'jiraSoftwareReleasesApprovers',
				teamName: 'fusion-solaris',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		showFlag({
			title: formatMessage(messages.genericUpdateFailedFlagTitle),
			description: formatMessage(messages.statusFailedFlagDescription),
			type: 'error',
		});
	};

	const handleChange = (option: JiraVersionApproverStatus) => {
		updateApproverStatus({
			variables: {
				input: {
					approverId: approverAri,
					status: option,
				},
			},

			optimisticResponse: {
				jira: {
					updateJiraVersionApproverStatus: {
						success: true,
						errors: null,
						approver: {
							id: approverAri,
							status: option,
						},
					},
				},
			},

			onCompleted: (data: approvalStatus_softwareReleasesApproversMutation$data) => {
				if (data.jira?.updateJiraVersionApproverStatus?.success !== true) {
					const responseErrors = data?.jira?.updateJiraVersionApproverStatus?.errors;
					const errorMessage =
						responseErrors?.[0]?.message ?? 'addVersionApprover graphql mutation failed';
					onMutationError(new Error(errorMessage));
					return;
				}
				experienceSuccess();

				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'selected',
						actionSubject: 'versionApproverStatus',
					}),
					'versionApproverStatusSelection',
					{ versionApproverOldStatus: approver.status, versionApproverNewStatus: option },
				);
			},
			onError: (error: Error) => {
				onMutationError(error);
			},
		});
		setIsStatusOpen(false);
	};

	const statusOptions = (
		<PopupContainer>
			{!isApproved ? (
				<ButtonItem
					data-ignore-target={STATUS_APPROVED_BUTTON_CLICK_IGNORE_TARGET}
					onClick={() => {
						experienceStart();
						handleChange('APPROVED');
					}}
				>
					{formatMessage(messages.approveOptionLabel)}
				</ButtonItem>
			) : (
				<ButtonItem
					data-ignore-target={STATUS_PENDING_BUTTON_CLICK_IGNORE_TARGET}
					onClick={() => {
						experienceStart();
						handleChange('PENDING');
					}}
				>
					{formatMessage(messages.revokeApprovalOptionLabel)}
				</ButtonItem>
			)}
			{!isDeclined && (
				<ButtonItem
					data-ignore-target={STATUS_DECLINED_BUTTON_CLICK_IGNORE_TARGET}
					onClick={() => {
						experienceStart();
						handleChange('DECLINED');
					}}
				>
					{formatMessage(messages.declineOptionLabel)}
				</ButtonItem>
			)}
		</PopupContainer>
	);

	return (
		<Popup
			onClose={() => setIsStatusOpen(false)}
			isOpen={isStatusOpen}
			placement="bottom-end"
			content={() => statusOptions}
			offset={[0, 8]}
			trigger={({ ...triggerProps }) => (
				<StyledButton
					data-ignore-target={STATUS_POPUP_TRIGGER_CLICK_IGNORE_TARGET}
					{...triggerProps}
					onClick={handleClick}
					isDisabled={!canChangeStatus}
				>
					<Lozenge appearance={mapApprovalStateToLozengeAppearance(approver.status)}>
						<IconWrapper>
							{approver.status}
							{canChangeStatus && (
								<ChevronDownIcon size="small" label={formatMessage(messages.buttonLabel)} />
							)}
						</IconWrapper>
					</Lozenge>
				</StyledButton>
			)}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		backgroundColor: 'transparent',
		margin: 0,
		width: 'auto',
		height: 'auto',
		padding: 0,
		border: 'transparent',
		borderRadius: '5px',

		outline: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > span': { margin: 0 },
		'&:focus, &:focus-within': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			boxShadow: `0 0 0 2px ${token('color.border.focused', colors.B200)}`,
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupContainer = styled.div({
	display: 'flex',
	width: '140px',
	padding: `${token('space.100', '8px')} 0px`,
	flexDirection: 'column',
	alignItems: 'flex-start',
});
